import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Plus, Layers, Edit2, Trash2, Users, X, Check,
  ChevronLeft, ChevronRight, AlertCircle, ChevronDown
} from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

const COLOR_SWATCHES = [
  "#3b82f6", "#10b981", "#8b5cf6", "#ec4899",
  "#f59e0b", "#ef4444", "#06b6d4", "#64748b"
];

const COLOR_NAMES = {
  "#3b82f6": "Blue",
  "#10b981": "Green",
  "#8b5cf6": "Purple",
  "#ec4899": "Pink",
  "#f59e0b": "Amber",
  "#ef4444": "Red",
  "#06b6d4": "Cyan",
  "#64748b": "Slate"
};

const channelOptions = [
  { value: "", label: "All Channels" },
  { value: "email", label: "Email" },
  { value: "freshchat", label: "Freshchat" },
  { value: "freshdesk", label: "Freshdesk" },
  { value: "slack", label: "Slack" },
  { value: "telegram", label: "Telegram" },
  { value: "webchat", label: "Webchat" },
  { value: "webhook", label: "Webhook" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "widget", label: "Widget Chat" },
];

function getChannelBadge(chanRaw) {
  const chan = String(chanRaw || "widget").toLowerCase();
  let label = "Widget Chat";
  let bg = "bg-blue-50 text-blue-700 border-blue-200/60";

  if (chan === "webchat") {
    label = "Webchat";
    bg = "bg-indigo-50 text-indigo-700 border-indigo-200/60";
  } else if (chan === "whatsapp") {
    label = "WhatsApp";
    bg = "bg-emerald-50 text-emerald-700 border-emerald-200/60";
  } else if (chan === "telegram") {
    label = "Telegram";
    bg = "bg-sky-50 text-sky-700 border-sky-200/60";
  } else if (chan === "slack") {
    label = "Slack";
    bg = "bg-purple-50 text-purple-700 border-purple-200/60";
  } else if (chan === "email") {
    label = "Email";
    bg = "bg-amber-50 text-amber-700 border-amber-200/60";
  } else if (chan === "freshdesk") {
    label = "Freshdesk";
    bg = "bg-emerald-50 text-emerald-700 border-emerald-200/60";
  } else if (chan === "freshchat") {
    label = "Freshchat";
    bg = "bg-teal-50 text-teal-700 border-teal-200/60";
  } else if (chan === "webhook") {
    label = "Webhook";
    bg = "bg-rose-50 text-rose-700 border-rose-200/60";
  } else if (chan === "zendesk") {
    label = "Zendesk";
    bg = "bg-orange-50 text-orange-700 border-orange-200/60";
  }

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border ${bg} whitespace-nowrap`}>
      {label}
    </span>
  );
}

export default function Segments() {
  const { showToast } = useToast();
  const navigate = useNavigate();

  // ── Segments State ──────────────────────────────────────────────────────────
  const [segments, setSegments] = useState([]);
  const [segmentsLoading, setSegmentsLoading] = useState(true);

  // Add / Edit Segment form state
  const [mode, setMode] = useState("idle"); // "idle" | "add" | "edit"
  const [editingSegment, setEditingSegment] = useState(null);
  const [segmentName, setSegmentName] = useState("");
  const [segmentDesc, setSegmentDesc] = useState("");
  const [segmentColor, setSegmentColor] = useState("#3b82f6");
  const [selectedContactIds, setSelectedContactIds] = useState(new Set());
  const [isSaving, setIsSaving] = useState(false);

  // Detail drawer state (right-side drawer)
  const [detailSegment, setDetailSegment] = useState(null);
  const [detailContacts, setDetailContacts] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Delete Segment confirm state
  const [segDeleteModal, setSegDeleteModal] = useState({ open: false, segment: null, loading: false });

  // ── Contacts Right Panel State ──────────────────────────────────────────────
  const [contacts, setContacts] = useState([]);
  const [totalContacts, setTotalContacts] = useState(0);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [contactsError, setContactsError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [isChannelDropdownOpen, setIsChannelDropdownOpen] = useState(false);
  const channelDropdownRef = useRef(null);
  const itemsPerPage = 10;

  const activeAiAgentId = ApiClient.currentAiAgentId;

  // ── Close channel dropdown on outside click ─────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (channelDropdownRef.current && !channelDropdownRef.current.contains(e.target)) {
        setIsChannelDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Debounce search ─────────────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchTerm); setCurrentPage(1); }, 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // ── Fetch Segments ──────────────────────────────────────────────────────────
  const fetchSegments = useCallback(async () => {
    setSegmentsLoading(true);
    try {
      const res = await ApiClient.getSegments();
      setSegments(res.segments || []);
    } catch {
      showToast("Failed to load segments", "error");
    } finally {
      setSegmentsLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchSegments(); }, [fetchSegments, activeAiAgentId]);

  // ── Fetch Contacts (right panel) ────────────────────────────────────────────
  const fetchContacts = useCallback(async (page = currentPage, search = debouncedSearch, channels = selectedChannels.join(",")) => {
    setContactsLoading(true);
    setContactsError(null);
    try {
      const res = await ApiClient.getContacts(page, itemsPerPage, search, channels);
      setContacts(res?.contacts || []);
      setTotalContacts(res?.total_count || 0);
    } catch {
      setContactsError("Failed to load contacts");
    } finally {
      setContactsLoading(false);
    }
  }, [currentPage, debouncedSearch, selectedChannels, itemsPerPage]);

  useEffect(() => {
    fetchContacts(currentPage, debouncedSearch, selectedChannels.join(","));
  }, [activeAiAgentId, currentPage, debouncedSearch, selectedChannels, fetchContacts]);

  // ── Segment Detail: load contacts ─────────────────────────────────────────
  const loadDetailContacts = useCallback(async (segment) => {
    if (!segment || !segment.contact_ids?.length) { setDetailContacts([]); return; }
    setDetailLoading(true);
    try {
      const res = await ApiClient.getContacts(1, 100);
      setDetailContacts((res?.contacts || []).filter(c => segment.contact_ids.includes(String(c.id))));
    } catch { setDetailContacts([]); }
    finally { setDetailLoading(false); }
  }, []);

  useEffect(() => {
    if (detailSegment) loadDetailContacts(detailSegment);
    else setDetailContacts([]);
  }, [detailSegment, loadDetailContacts]);

  // ── Segment CRUD handlers ───────────────────────────────────────────────────
  const startAdd = () => {
    setEditingSegment(null);
    setSegmentName("");
    setSegmentDesc("");
    setSegmentColor("#3b82f6");
    setSelectedContactIds(new Set());
    setDetailSegment(null);
    setMode("add");
  };

  const startEdit = (seg) => {
    setEditingSegment(seg);
    setSegmentName(seg.name);
    setSegmentDesc(seg.description || "");
    setSegmentColor(seg.color || "#3b82f6");
    setSelectedContactIds(new Set(seg.contact_ids.map(String)));
    setDetailSegment(null);
    setMode("edit");
  };

  const cancelMode = () => { setMode("idle"); setSelectedContactIds(new Set()); };

  const handleToggleContact = (contactId) => {
    const idStr = String(contactId);
    let transitionToAdd = false;
    setSelectedContactIds(prev => {
      const next = new Set(prev);
      if (next.has(idStr)) {
        next.delete(idStr);
      } else {
        next.add(idStr);
      }
      if (next.size > 0 && mode === "idle") {
        transitionToAdd = true;
      }
      return next;
    });
    if (transitionToAdd) {
      setMode("add");
    }
  };

  const handleSaveSegment = async () => {
    if (!segmentName.trim()) { showToast("Segment name is required", "warning"); return; }
    if (selectedContactIds.size === 0) {
      showToast("Please select at least one contact to create a segment", "warning");
      return;
    }
    setIsSaving(true);
    const payload = {
      name: segmentName.trim(),
      description: segmentDesc.trim() || null,
      color: segmentColor,
      contact_ids: Array.from(selectedContactIds)
    };
    try {
      if (mode === "edit") {
        await ApiClient.updateSegment(editingSegment.id, payload);
        showToast("Segment updated", "success");
      } else {
        await ApiClient.createSegment(payload);
        showToast("Segment created", "success");
      }
      setMode("idle");
      setSelectedContactIds(new Set());
      fetchSegments();
    } catch { showToast("Failed to save segment", "error"); }
    finally { setIsSaving(false); }
  };

  const confirmDeleteSegment = async () => {
    setSegDeleteModal(p => ({ ...p, loading: true }));
    try {
      await ApiClient.deleteSegment(segDeleteModal.segment.id);
      showToast("Segment deleted", "success");
      if (detailSegment?.id === segDeleteModal.segment.id) setDetailSegment(null);
      fetchSegments();
    } catch { showToast("Failed to delete segment", "error"); }
    finally { setSegDeleteModal({ open: false, segment: null, loading: false }); }
  };

  const startItem = totalContacts === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalContacts);
  const totalPages = Math.ceil(totalContacts / itemsPerPage);
  const isAddEditMode = mode === "add" || mode === "edit";
  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8 space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">Contact Segments</h1>
            <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
              Organize target audiences into smart segments and select contacts directly from your contact database.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!isAddEditMode ? (
              <button
                onClick={startAdd}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-colors font-semibold text-xs shadow-xs cursor-pointer"
              >
                <Plus size={14} />
                <span>Add Segment</span>
              </button>
            ) : (
              <button
                onClick={cancelMode}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 border border-zinc-200 text-zinc-700 bg-white hover:bg-zinc-50 rounded-lg transition-colors font-semibold text-xs shadow-2xs cursor-pointer"
              >
                <X size={14} />
                <span>Cancel {mode === "edit" ? "Edit" : "Create"}</span>
              </button>
            )}
          </div>
        </div>

        {/* Dual Layout: Left Segment Column + Right Contacts Table */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
          {/* ════════════════ LEFT COLUMN: Segments List / Inline Builder ════════════════ */}
          <div className="lg:col-span-4 space-y-3">
            {/* Inline Add / Edit Card */}
            {isAddEditMode && (
              <div className="bg-white border-2 border-zinc-900 rounded-xl p-4 shadow-sm space-y-3.5 transition-all">
                <div className="flex items-center justify-between border-b border-zinc-100 pb-2.5">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: segmentColor }}
                    />
                    <h2 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">
                      {mode === "edit" ? "Edit Segment" : "New Segment"}
                    </h2>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 bg-blue-50 border border-blue-200/60 px-2 py-0.5 rounded-md">
                    <Users size={11} /> {selectedContactIds.size} selected
                  </span>
                </div>

                <div className="space-y-2.5">
                  <div>
                    <label className="block text-[11px] font-medium text-zinc-700 mb-1">
                      Segment Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. VIP Customers, WhatsApp Leads..."
                      value={segmentName}
                      onChange={(e) => setSegmentName(e.target.value)}
                      className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:bg-white text-zinc-900 placeholder:text-zinc-400"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-medium text-zinc-700 mb-1">
                      Description (Optional)
                    </label>
                    <textarea
                      rows={2}
                      placeholder="Brief note on who belongs in this segment..."
                      value={segmentDesc}
                      onChange={(e) => setSegmentDesc(e.target.value)}
                      className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:bg-white text-zinc-900 placeholder:text-zinc-400 resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-medium text-zinc-700 mb-1.5">
                      Color Tag
                    </label>
                    <div className="flex items-center gap-2 flex-wrap">
                      {COLOR_SWATCHES.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setSegmentColor(color)}
                          className={`w-6 h-6 rounded-full flex items-center justify-center transition-transform cursor-pointer ${
                            segmentColor === color ? "ring-2 ring-zinc-900 ring-offset-1 scale-110" : "hover:scale-105"
                          }`}
                          style={{ backgroundColor: color }}
                          title={COLOR_NAMES[color]}
                        >
                          {segmentColor === color && <Check size={12} className="text-white" />}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="pt-2 border-t border-zinc-100 flex items-center gap-2 justify-end">
                  <button
                    type="button"
                    onClick={cancelMode}
                    className="px-3 py-1.5 text-xs font-semibold text-zinc-600 hover:text-zinc-900 bg-white border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={selectedContactIds.size === 0 || isSaving}
                    onClick={handleSaveSegment}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold text-white bg-zinc-900 rounded-lg hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    {isSaving ? "Saving..." : mode === "edit" ? "Save Changes" : "Create Segment"}
                  </button>
                </div>
              </div>
            )}

            {/* Segments Directory Header */}
            <div className="flex items-center justify-between px-1">
              <span className="text-xs font-bold text-zinc-800 uppercase tracking-wider flex items-center gap-1.5">
                <Layers size={13} className="text-zinc-500" />
                Segments ({segments.length})
              </span>
              <span className="text-[11px] text-zinc-400">Click to view members</span>
            </div>

            {/* Segments Cards List */}
            <div className="space-y-2 max-h-[640px] overflow-y-auto pr-0.5">
              {segmentsLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="p-3.5 rounded-xl border border-zinc-200/70 bg-white space-y-2 animate-pulse">
                    <div className="flex items-center justify-between">
                      <div className="h-3.5 bg-zinc-200 rounded w-28" />
                      <div className="h-3 bg-zinc-200 rounded w-8" />
                    </div>
                    <div className="h-2.5 bg-zinc-100 rounded w-44" />
                  </div>
                ))
              ) : segments.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-xs font-semibold text-zinc-700">No segments created yet</p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Click "Add Segment" or check contacts on the right to start.</p>
                </div>
              ) : (
                segments.map((seg) => {
                  const isActive = detailSegment?.id === seg.id;
                  const isEditing = editingSegment?.id === seg.id && isAddEditMode;
                  return (
                    <div
                      key={seg.id}
                      onClick={() => {
                        if (!isAddEditMode) {
                          setDetailSegment(isActive ? null : seg);
                        }
                      }}
                      className={`p-3 rounded-xl border transition-all cursor-pointer ${
                        isEditing
                          ? "border-zinc-900 bg-zinc-50/50 shadow-xs"
                          : isActive
                            ? "border-zinc-400 bg-zinc-50/80 shadow-xs ring-1 ring-zinc-400"
                            : "border-zinc-200/80 bg-white hover:border-zinc-300 hover:shadow-xs"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <div
                            className="w-2.5 h-2.5 rounded-full shrink-0"
                            style={{ backgroundColor: seg.color || "#3b82f6" }}
                          />
                          <h3 className="text-xs font-bold text-zinc-900 truncate">{seg.name}</h3>
                        </div>

                        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => startEdit(seg)}
                            className="p-1 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded transition-colors"
                            title="Edit Segment"
                          >
                            <Edit2 size={12} />
                          </button>
                          <button
                            onClick={() => setSegDeleteModal({ open: true, segment: seg, loading: false })}
                            className="p-1 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Delete Segment"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>

                      {seg.description && (
                        <p className="text-[11px] text-zinc-500 mt-1 line-clamp-2 pl-4.5">
                          {seg.description}
                        </p>
                      )}

                      <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-zinc-100 pl-4.5">
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-zinc-600">
                          <Users size={11} className="text-zinc-400" />
                          {seg.contact_ids?.length || 0} Contacts
                        </span>
                        <span className="text-[10px] text-zinc-400 font-mono">
                          {COLOR_NAMES[seg.color] || "Tag"}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* ════════════════ RIGHT COLUMN: Contacts Selection Table ════════════════ */}
          <div className="lg:col-span-8 space-y-3">
            {/* Table Toolbar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
              <div className="flex items-center gap-2 flex-wrap flex-1">
                {/* Search Input */}
                <div className="relative w-full sm:w-56">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input
                    type="text"
                    placeholder="Search contacts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:bg-white text-zinc-900 placeholder:text-zinc-400 transition-colors"
                  />
                </div>

                {/* Channel Filter Dropdown */}
                <div ref={channelDropdownRef} className="relative">
                  <button
                    onClick={() => setIsChannelDropdownOpen(!isChannelDropdownOpen)}
                    className="inline-flex items-center justify-between gap-2 px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer min-w-[130px]"
                  >
                    <span className="truncate">
                      {selectedChannels.length === 0
                        ? "All Channels"
                        : selectedChannels.length === 1
                          ? channelOptions.find((o) => o.value === selectedChannels[0])?.label
                          : `${selectedChannels.length} Channels`}
                    </span>
                    <ChevronDown
                      size={14}
                      className={`text-zinc-500 transition-transform ${isChannelDropdownOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {isChannelDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-zinc-200 rounded-lg shadow-lg py-1 z-50">
                      {channelOptions.map((opt) => {
                        const isSel = opt.value === "" ? selectedChannels.length === 0 : selectedChannels.includes(opt.value);
                        return (
                          <div
                            key={opt.value}
                            onClick={() => {
                              if (opt.value === "") {
                                setSelectedChannels([]);
                              } else {
                                const next = [...selectedChannels];
                                const idx = next.indexOf(opt.value);
                                idx > -1 ? next.splice(idx, 1) : next.push(opt.value);
                                setSelectedChannels(next);
                              }
                              setCurrentPage(1);
                            }}
                            className={`flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-zinc-50 ${
                              isSel ? "text-blue-600 font-semibold bg-blue-50/50" : "text-zinc-700"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSel}
                              readOnly
                              className="rounded border-zinc-300 text-zinc-900 pointer-events-none"
                            />
                            <span>{opt.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {isAddEditMode && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-zinc-500 bg-zinc-100 px-2.5 py-1 rounded-lg">
                    Check contacts below to include in segment
                  </span>
                )}
              </div>

              {/* Pagination controls */}
              <div className="flex items-center gap-3 self-end sm:self-auto">
                <span className="text-[11px] text-zinc-500">
                  {startItem}–{endItem} of {totalContacts}
                </span>
                <div className="inline-flex items-center border border-zinc-200 rounded-lg bg-zinc-50 p-0.5">
                  <button
                    disabled={currentPage === 1 || contactsLoading}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    className="p-1 rounded text-zinc-600 hover:text-zinc-900 hover:bg-white disabled:opacity-40 disabled:hover:bg-transparent transition-colors cursor-pointer"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <button
                    disabled={currentPage >= totalPages || contactsLoading}
                    onClick={() => setCurrentPage((p) => p + 1)}
                    className="p-1 rounded text-zinc-600 hover:text-zinc-900 hover:bg-white disabled:opacity-40 disabled:hover:bg-transparent transition-colors cursor-pointer"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            </div>

            {/* Table Card */}
            <div className="border border-zinc-200 rounded-xl bg-white overflow-hidden shadow-2xs">
              <div className="overflow-x-auto min-h-[440px]">
                {contactsError ? (
                  <div className="p-8 text-center text-red-500 space-y-2">
                    <AlertCircle size={24} className="mx-auto" />
                    <p className="text-xs font-semibold">{contactsError}</p>
                  </div>
                ) : (
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-zinc-100 bg-zinc-50/50 text-[11px] font-semibold text-zinc-500">
                        <th className="py-2.5 px-3 w-10 text-center">
                          <input
                            type="checkbox"
                            className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900 cursor-pointer"
                            checked={contacts.length > 0 && contacts.every((c) => selectedContactIds.has(String(c.id)))}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setSelectedContactIds((prev) => {
                                const next = new Set(prev);
                                contacts.forEach((c) => {
                                  if (checked) next.add(String(c.id));
                                  else next.delete(String(c.id));
                                });
                                return next;
                              });
                              if (checked && mode === "idle") {
                                setMode("add");
                              }
                            }}
                            title="Select all on this page"
                          />
                        </th>
                        <th className="py-2.5 px-3 min-w-[150px]">Contact</th>
                        <th className="py-2.5 px-3 min-w-[110px]">Username</th>
                        <th className="py-2.5 px-3 min-w-[100px]">Channel</th>
                        <th className="py-2.5 px-3 min-w-[130px]">Email</th>
                        <th className="py-2.5 px-3 min-w-[110px]">Phone</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {contactsLoading ? (
                        Array.from({ length: 8 }).map((_, i) => (
                          <tr key={i} className="animate-pulse">
                            <td className="py-3 px-3 text-center">
                              <div className="w-3.5 h-3.5 bg-zinc-200 rounded mx-auto" />
                            </td>
                            <td className="py-3 px-3">
                              <div className="flex items-center gap-2.5">
                                <div className="w-7 h-7 bg-zinc-200 rounded-full" />
                                <div className="h-3 bg-zinc-200 rounded w-20" />
                              </div>
                            </td>
                            <td className="py-3 px-3"><div className="h-3 bg-zinc-200 rounded w-16" /></td>
                            <td className="py-3 px-3"><div className="h-4 bg-zinc-100 rounded w-16" /></td>
                            <td className="py-3 px-3"><div className="h-3 bg-zinc-200 rounded w-24" /></td>
                            <td className="py-3 px-3"><div className="h-3 bg-zinc-200 rounded w-16" /></td>
                          </tr>
                        ))
                      ) : contacts.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="py-12 text-center text-zinc-400">
                            <Users size={28} className="mx-auto text-zinc-300 mb-1" />
                            <p className="text-xs font-medium">No contacts match your criteria</p>
                          </td>
                        </tr>
                      ) : (
                        contacts.map((contact) => {
                          const params = typeof contact.custom_parameters === "string" ? JSON.parse(contact.custom_parameters || "{}") : (contact.custom_parameters || {});
                          const imgUrl = params?.avatar_url
                            ? `${(process.env.REACT_APP_API_BASE_URL || "").replace(/\/api$/, "")}${params.avatar_url}`
                            : `https://api.dicebear.com/7.x/micah/svg?seed=${params?.avatar_seed || contact.name || contact.userid || String(contact.id)}&backgroundColor=b6e3f4,c0aede,d1d4f9`;
                          const uid = contact.userid;
                          const uuidReg = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                          const displayUid = !uid || uid === "anonymous" || uuidReg.test(uid) || /^(widget|telegram|slack|freshchat|freshdesk|zendesk|email|whatsapp|webchat)_/i.test(uid) ? "anonymous" : uid;
                          const em = contact.email;
                          const ph = contact.phone;
                          const isChecked = selectedContactIds.has(String(contact.id));

                          return (
                            <tr
                              key={contact.id}
                              onClick={() => {
                                if (isAddEditMode) {
                                  handleToggleContact(contact.id);
                                } else {
                                  navigate(`/contacts/${contact.id}`);
                                }
                              }}
                              className={`transition-colors cursor-pointer ${
                                isChecked ? "bg-blue-50/40" : "hover:bg-zinc-50/70"
                              }`}
                            >
                              <td className="py-2.5 px-3 text-center" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900 cursor-pointer"
                                  checked={isChecked}
                                  onChange={() => handleToggleContact(contact.id)}
                                />
                              </td>
                              <td className="py-2.5 px-3">
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <img
                                    src={imgUrl}
                                    alt="Avatar"
                                    className="w-7 h-7 rounded-full object-cover border border-zinc-200 shrink-0"
                                  />
                                  <span className="font-semibold text-zinc-900 truncate">
                                    {contact.name || "Unknown"}
                                  </span>
                                </div>
                              </td>
                              <td className="py-2.5 px-3 font-mono text-[11px] text-zinc-600 truncate max-w-[130px]">
                                {displayUid}
                              </td>
                              <td className="py-2.5 px-3">
                                {getChannelBadge(params?.channel)}
                              </td>
                              <td className="py-2.5 px-3 text-zinc-600 truncate max-w-[150px]">
                                {!em || em === "N/A" ? (
                                  <span className="text-zinc-300">—</span>
                                ) : (
                                  <a
                                    href={`mailto:${em}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-zinc-700 hover:text-blue-600 hover:underline"
                                  >
                                    {em}
                                  </a>
                                )}
                              </td>
                              <td className="py-2.5 px-3 text-zinc-600 truncate max-w-[120px]">
                                {!ph || ph === "N/A" ? <span className="text-zinc-300">—</span> : ph}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ════════════════ Segment Detail Drawer ════════════════ */}
        {detailSegment && !isAddEditMode && (
          <div className="fixed inset-0 z-50 flex justify-end">
            <div
              className="fixed inset-0 bg-zinc-900/30 backdrop-blur-sm transition-opacity"
              onClick={() => setDetailSegment(null)}
            />
            <div className="relative w-full max-w-lg bg-white h-full shadow-2xl flex flex-col z-10 animate-in slide-in-from-right duration-200">
              {/* Drawer Header */}
              <div className="p-4 sm:p-5 border-b border-zinc-100 flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <div
                    className="w-3.5 h-3.5 rounded-full shrink-0"
                    style={{ backgroundColor: detailSegment.color || "#3b82f6" }}
                  />
                  <div>
                    <h2 className="text-sm font-bold text-zinc-900">{detailSegment.name}</h2>
                    {detailSegment.description && (
                      <p className="text-xs text-zinc-500 mt-0.5">{detailSegment.description}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setDetailSegment(null)}
                  className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Drawer Capacity Stats */}
              <div className="px-5 py-3 bg-zinc-50/60 border-b border-zinc-100 flex items-center gap-6">
                <div>
                  <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Total Members</span>
                  <div className="text-sm font-bold text-zinc-900 mt-0.5">{detailContacts.length} Contacts</div>
                </div>
                <div className="border-l border-zinc-200 pl-6">
                  <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Tag Color</span>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <div
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: detailSegment.color || "#3b82f6" }}
                    />
                    <span className="text-xs font-semibold text-zinc-700">
                      {COLOR_NAMES[detailSegment.color] || "Tag"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Drawer Member Contacts List */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-2">
                <div className="flex items-center justify-between pb-2">
                  <span className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider">
                    Segment Member List
                  </span>
                  <button
                    onClick={() => {
                      const seg = detailSegment;
                      setDetailSegment(null);
                      startEdit(seg);
                    }}
                    className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1 cursor-pointer"
                  >
                    <Edit2 size={11} /> Edit Segment
                  </button>
                </div>

                {detailLoading ? (
                  <div className="py-12 text-center text-zinc-400">
                    <div className="w-5 h-5 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-xs">Loading member contacts...</p>
                  </div>
                ) : detailContacts.length === 0 ? (
                  <div className="py-12 text-center">
                    <p className="text-xs font-semibold text-zinc-700">No contacts in this segment</p>
                    <p className="text-[11px] text-zinc-400 mt-0.5">Edit this segment to add contacts.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-zinc-100 border border-zinc-200 rounded-xl overflow-hidden bg-white">
                    {detailContacts.map((c) => {
                      const params = typeof c.custom_parameters === "string" ? JSON.parse(c.custom_parameters || "{}") : (c.custom_parameters || {});
                      const imgUrl = params?.avatar_url
                        ? `${(process.env.REACT_APP_API_BASE_URL || "").replace(/\/api$/, "")}${params.avatar_url}`
                        : `https://api.dicebear.com/7.x/micah/svg?seed=${params?.avatar_seed || c.name || c.userid || String(c.id)}&backgroundColor=b6e3f4,c0aede,d1d4f9`;
                      return (
                        <div
                          key={c.id}
                          onClick={() => navigate(`/contacts/${c.id}`)}
                          className="p-3 flex items-center justify-between hover:bg-zinc-50/80 cursor-pointer transition-colors"
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <img
                              src={imgUrl}
                              alt="Avatar"
                              className="w-7 h-7 rounded-full object-cover border border-zinc-200 shrink-0"
                            />
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-zinc-900 truncate">{c.name || "Unknown"}</p>
                              <p className="text-[11px] text-zinc-400 truncate">{c.email || c.phone || "No direct contact"}</p>
                            </div>
                          </div>
                          <div className="shrink-0 pl-2">
                            {getChannelBadge(params?.channel)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Delete Segment Confirm Modal ── */}
        <DeleteConfirmModal
          isOpen={segDeleteModal.open}
          onClose={() => setSegDeleteModal({ open: false, segment: null, loading: false })}
          onConfirm={confirmDeleteSegment}
          title="Delete Segment"
          message={`Are you sure you want to delete "${segDeleteModal.segment?.name}"? This action cannot be undone.`}
          confirmText="Delete"
          isLoading={segDeleteModal.loading}
        />
      </div>
    </div>
  );
}
  